import { useState, useEffect } from 'react';
import { teamsAPI, groundsAPI } from '../../services/api';
import { PageHeader, SectionTitle, Field, Spinner } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const EMPTY_UMPIRE = { name: '', contact: '', is_certified: false };

export default function MyTeamPage() {
  const { user } = useAuthStore();
  const [team, setTeam]       = useState(null);
  const [grounds, setGrounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  // Editable fields
  const [form, setForm]       = useState({});
  const [umpires, setUmpires] = useState([{ ...EMPTY_UMPIRE }]);

  useEffect(() => {
    if (!user?.team_id) { setLoading(false); return; }
    Promise.all([
      teamsAPI.get(user.team_id),
      groundsAPI.list(),
    ]).then(([t, g]) => {
      const td = t.data;
      setTeam(td);
      setGrounds(g.data);
      setForm({
        captain_name:    td.captain_name    || '',
        captain_contact: td.captain_contact || '',
        vc_name:         td.vc_name         || '',
        vc_contact:      td.vc_contact      || '',
        home_ground_id:  td.home_ground_id  || '',
      });
      setUmpires(Array.isArray(td.umpires) && td.umpires.length > 0
        ? td.umpires.map(u => ({ name: u.name || '', contact: u.contact || '', is_certified: !!u.is_certified }))
        : [{ ...EMPTY_UMPIRE }]
      );
    }).catch(() => toast.error('Failed to load team'))
      .finally(() => setLoading(false));
  }, [user?.team_id]);

  const sf = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const updateUmpire = (i, k, v) => setUmpires(p => p.map((u, idx) => idx === i ? { ...u, [k]: v } : u));
  const addUmpire    = () => setUmpires(p => [...p, { ...EMPTY_UMPIRE }]);
  const removeUmpire = (i) => setUmpires(p => p.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await teamsAPI.update(user.team_id, {
        ...form,
        home_ground_id: form.home_ground_id || null,
        umpires: umpires.filter(u => u.name.trim()),
      });
      toast.success('Team info saved!');
      // Refresh team
      const { data } = await teamsAPI.get(user.team_id);
      setTeam(data);
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}><Spinner /></div>;

  if (!user?.team_id) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
      <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>No team assigned</div>
      <div style={{ color: 'var(--mid)', fontSize: 14 }}>Your account hasn't been linked to a team yet. Please contact your administrator.</div>
    </div>
  );

  const TABS = [['info', 'Team Info'], ['captain', 'Captain & VC'], ['umpires', 'Umpires']];

  return (
    <div>
      <PageHeader icon="◉" title={team?.name || 'My Team'}
        subtitle={[team?.division_name, team?.pool ? `Pool ${team.pool}` : null, team?.series_name].filter(Boolean).join(' · ')}
        actions={
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <><Spinner size={14} /> Saving…</> : '✓ Save Changes'}
          </button>
        }
      />

      {/* Team summary card */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Short Name',   value: team?.short_name,       color: 'var(--accent)' },
          { label: 'Division',     value: team?.division_name,    color: 'var(--blue)' },
          { label: 'Pool',         value: team?.pool ? `Pool ${team.pool}` : '—', color: 'var(--gold)' },
          { label: 'Home Ground',  value: team?.home_ground_name || '—', color: 'var(--purple)' },
          { label: 'CricClubs',    value: team?.cricclubs_team_id ? `#${team.cricclubs_team_id}` : 'Not linked', color: team?.cricclubs_team_id ? 'var(--accent)' : 'var(--dim)' },
          { label: 'Status',       value: team?.is_blacklisted ? 'Blacklisted' : 'Active', color: team?.is_blacklisted ? 'var(--red)' : 'var(--accent)' },
        ].map(c => (
          <div key={c.label} className="card" style={{ padding: '14px 16px', borderTop: `3px solid ${c.color}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{c.value || '—'}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(([t, label]) => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: activeTab === t ? 700 : 500,
            color: activeTab === t ? 'var(--accent)' : 'var(--mid)',
            borderBottom: activeTab === t ? '2px solid var(--accent)' : '2px solid transparent',
            fontSize: 13, marginBottom: -1
          }}>{label}</button>
        ))}
      </div>

      <div className="card" style={{ padding: 24 }}>

        {/* ── Team Info ── */}
        {activeTab === 'info' && (
          <div>
            <SectionTitle>Team Details</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mid)', textTransform: 'uppercase', marginBottom: 4 }}>Full Name</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{team?.name}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mid)', textTransform: 'uppercase', marginBottom: 4 }}>Short Name</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--accent)' }}>{team?.short_name}</div>
              </div>
            </div>
            <Field label="Home Ground" note="Your team's preferred home ground">
              <select className="input select" value={form.home_ground_id} onChange={e => sf('home_ground_id', e.target.value)}>
                <option value="">No home ground</option>
                {grounds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </Field>
            <div style={{ marginTop: 20, padding: '12px 16px', background: 'rgba(0,200,150,.06)', border: '1px solid rgba(0,200,150,.2)', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: 'var(--mid)' }}>
                To update your team name, division, or pool — contact your league administrator.
              </div>
            </div>
          </div>
        )}

        {/* ── Captain & VC ── */}
        {activeTab === 'captain' && (
          <div>
            <SectionTitle>Captain</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
              <Field label="Captain Name">
                <input className="input" placeholder="Full name" value={form.captain_name} onChange={e => sf('captain_name', e.target.value)} />
              </Field>
              <Field label="Captain Contact" note="Phone or email">
                <input className="input" placeholder="+1 xxx xxx xxxx" value={form.captain_contact} onChange={e => sf('captain_contact', e.target.value)} />
              </Field>
            </div>
            <SectionTitle>Vice Captain</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Vice Captain Name">
                <input className="input" placeholder="Full name" value={form.vc_name} onChange={e => sf('vc_name', e.target.value)} />
              </Field>
              <Field label="VC Contact" note="Phone or email">
                <input className="input" placeholder="+1 xxx xxx xxxx" value={form.vc_contact} onChange={e => sf('vc_contact', e.target.value)} />
              </Field>
            </div>
          </div>
        )}

        {/* ── Umpires ── */}
        {activeTab === 'umpires' && (
          <div>
            <SectionTitle color="var(--purple)">Umpires</SectionTitle>
            <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 16 }}>
              Add umpires associated with your team. Certified umpires are preferred for scheduling.
            </p>
            {umpires.map((u, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 10, alignItems: 'center', marginBottom: 12, padding: '12px 14px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <Field label={`Umpire ${i + 1}`}>
                  <input className="input" placeholder="Full name" value={u.name} onChange={e => updateUmpire(i, 'name', e.target.value)} />
                </Field>
                <Field label="Contact">
                  <input className="input" placeholder="Phone or email" value={u.contact} onChange={e => updateUmpire(i, 'contact', e.target.value)} />
                </Field>
                <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', color: 'var(--mid)', marginTop: 18 }}>
                  <input type="checkbox" checked={u.is_certified} onChange={e => updateUmpire(i, 'is_certified', e.target.checked)} />
                  Certified
                </label>
                {umpires.length > 1 && (
                  <button className="btn btn-ghost" onClick={() => removeUmpire(i)}
                    style={{ fontSize: 12, padding: '4px 8px', color: 'var(--red)', marginTop: 18 }}>✕</button>
                )}
              </div>
            ))}
            <button className="btn btn-ghost" onClick={addUmpire} style={{ marginTop: 4 }}>+ Add Umpire</button>
          </div>
        )}

        {/* Save button at bottom of form */}
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <><Spinner size={14} /> Saving…</> : '✓ Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
